/**
 * CombatLog Component - Combat Session History and Rewind Interface
 * 
 * This component provides a comprehensive combat log interface that displays:
 * - All combat actions, damage, healing, and conditions
 * - Turn-by-turn combat history with timestamps
 * - Rewind functionality to go back to previous combat states
 * - Export/import combat session data
 * 
 * @date_of_change 2025-08-30
 * - Initial implementation with full combat log display and rewind controls
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import CombatLogManager, { COMBAT_LOG_TYPES } from '../../utils/CombatLogManager';
import BattleRoomDialog from '../battle-room-v2/components/BattleRoomDialog';
import './CombatLog.css';

// Display the stored kept die for both single and paired rolls. The server
// records it as d20Roll. D&D Basic Rules, Advantage and Disadvantage.
// date_of_change: 2026-11-05
const recordedD20 = (roll) => roll.d20Roll ?? roll.chosenValue ?? '?';
// A committed outcome can carry an applied total without a roll object.
// App UI Standard, BattleRoom data display. date_of_change: 2026-11-05
const recordedDamageTotal = (damage) => typeof damage === 'number' ? damage : damage?.total;

const CombatLog = ({ 
  onRewindRequest,
  onExportSession,
  className = '',
  collapsed = false,
  isGameMaster = false,
  editMode = false,
  onEditModeChange = null,
  currentUser = null,
  authoritativeRewindPoints = null,
  rewindPending = false,
  rewindError = '',
  onRefreshRewindPoints = null,
  onRewindPreview = null
}) => {
  const [combatLog, setCombatLog] = useState([]);
  const [rewindPoints, setRewindPoints] = useState([]);
  const [selectedRewindPoint, setSelectedRewindPoint] = useState(-1);
  const [filter, setFilter] = useState('all');
  const [showRewindControls, setShowRewindControls] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  const [showRawFor, setShowRawFor] = useState(new Set());
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [rewindConfirmation, setRewindConfirmation] = useState(null);
  const [rewindPreview, setRewindPreview] = useState(null);
  const [rewindReason, setRewindReason] = useState('');
  const [previewPending, setPreviewPending] = useState(false);
  const [localRewindError, setLocalRewindError] = useState('');
  const displayedRewindPoints = Array.isArray(authoritativeRewindPoints)
    ? authoritativeRewindPoints
    : rewindPoints;

  // Refresh combat log data
  const refreshCombatLog = useCallback(() => {
    const log = CombatLogManager.getCombatLog();
    const points = CombatLogManager.getRewindPoints();
    setCombatLog(log);
    setRewindPoints(points);
  }, []);

  // Auto-refresh combat log periodically
  useEffect(() => {
    refreshCombatLog();
    const interval = setInterval(refreshCombatLog, 2000); // Refresh every 2 seconds
    return () => clearInterval(interval);
  }, [refreshCombatLog]);

  // Immediate refresh on engine/UI events
  // @date_of_change: 2025-10-09 - Listen for combat-log-updated and websocket-combat-log-entry events
  useEffect(() => {
    const handleImmediateRefresh = () => {
      refreshCombatLog();
    };

    window.addEventListener('combat-log-updated', handleImmediateRefresh);
    window.addEventListener('websocket-combat-log-entry', handleImmediateRefresh);

    return () => {
      window.removeEventListener('combat-log-updated', handleImmediateRefresh);
      window.removeEventListener('websocket-combat-log-entry', handleImmediateRefresh);
    };
  }, [refreshCombatLog]);

  // Handle rewind to specific point
  const handleRewind = useCallback(async () => {
    const point = rewindConfirmation;
    if (!point) return;
    setLocalRewindError('');
    try {
      if (onRewindRequest) {
        const result = await onRewindRequest(point, { preview: rewindPreview, reason: rewindReason.trim() });
        if (result === false || result?.ok === false) throw new Error(result?.message || 'Rewind was not applied.');
      } else if (Number.isInteger(point.index)) {
        CombatLogManager.rewindTo(point.index);
      }
      refreshCombatLog();
      setShowRewindControls(false);
      setSelectedRewindPoint(-1);
      setRewindConfirmation(null);
      setRewindPreview(null);
      setRewindReason('');
    } catch (cause) {
      setLocalRewindError(cause?.message || 'Rewind failed. Refresh the rewind list and try again.');
    }
  }, [onRewindRequest, refreshCombatLog, rewindConfirmation, rewindPreview, rewindReason]);

  const reviewRewind = useCallback(async () => {
    const point = displayedRewindPoints[selectedRewindPoint];
    if (!point) return;
    setLocalRewindError('');
    setPreviewPending(true);
    try {
      const preview = onRewindPreview ? await onRewindPreview(point) : null;
      setRewindPreview(preview);
      setRewindConfirmation(point);
    } catch (cause) {
      setLocalRewindError(cause?.message || 'The rewind preview could not be loaded.');
    } finally {
      setPreviewPending(false);
    }
  }, [displayedRewindPoints, onRewindPreview, selectedRewindPoint]);

  // Handle export combat session
  const handleExport = useCallback(() => {
    const sessionData = CombatLogManager.exportCombatSession();
    if (onExportSession) {
      onExportSession(sessionData);
    } else {
      // Default export to JSON file
      const dataStr = JSON.stringify(sessionData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `combat_session_${sessionData.combatId}.json`;
      link.click();
      URL.revokeObjectURL(url);
    }
  }, [onExportSession]);

  const beginEditing = useCallback((entry) => {
    if (!isGameMaster || !entry?.id) return;
    setEditingEntryId(entry.id);
    setEditDraft(entry.description || '');
  }, [isGameMaster]);

  const cancelEditing = useCallback(() => {
    setEditingEntryId(null);
    setEditDraft('');
  }, []);

  const saveEntryEdit = useCallback(() => {
    if (!isGameMaster || !editingEntryId || !editDraft.trim()) return;
    const updated = CombatLogManager.updateLogEntry(
      editingEntryId,
      { description: editDraft },
      {
        id: currentUser?.userId,
        name: currentUser?.userName || currentUser?.name || currentUser?.displayName || 'Game Master'
      }
    );
    if (updated) {
      refreshCombatLog();
      cancelEditing();
    }
  }, [isGameMaster, editingEntryId, editDraft, currentUser, refreshCombatLog, cancelEditing]);

  // GAP-012: Export log as Markdown file — date_of_change: 2026-05-20
  const handleExportMarkdown = useCallback(() => {
    const markdown = CombatLogManager.exportAsMarkdown();
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `combat_log_${CombatLogManager.combatId || Date.now()}.md`;
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  // GAP-012: Summary stats — date_of_change: 2026-05-20
  const [showSummary, setShowSummary] = useState(false);

  // Toggle entry expansion
  const toggleEntryExpansion = useCallback((entryIndex) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryIndex)) {
        newSet.delete(entryIndex);
      } else {
        newSet.add(entryIndex);
      }
      return newSet;
    });
  }, []);

  // Friendly detail renderer per type
  const renderFriendlyDetails = (entry) => {
    const d = entry?.data || {};
    switch (entry.type) {
      case COMBAT_LOG_TYPES.ACTION_STANDARD:
      case COMBAT_LOG_TYPES.ACTION_BONUS:
      case COMBAT_LOG_TYPES.ACTION_REACTION: {
        const actionName = d?.action?.originalAction?.name || d?.action?.name || d?.result?.actionName || 'Action';
        const effect = d?.result?.effect || d?.action?.description;
        const ruleReference = d?.result?.standardAction?.ruleReference || d?.action?.standardAction?.ruleReference;
        const check = d?.result?.check || d?.result?.skillCheck || d?.result?.abilityCheck;
        const checkRoll = d?.result?.checkRoll || check;
        const checkLabel = check?.skill || (check?.ability ? `${check.ability} ability` : 'Check');
        const checkOutcome = d?.result?.checkSuccess === undefined
          ? ''
          : d.result.checkSuccess ? ' — SUCCESS' : ' — FAILURE';

        return (
          <ul className="friendly-fields">
            <li><strong>Action:</strong> {actionName}</li>
            {Array.isArray(d?.targets) && d.targets.length > 0 && (
              <li><strong>Targets:</strong> {d.targets.map(t => t.name).join(', ')}</li>
            )}
            {effect && (
              <li><strong>Effect:</strong> {effect}</li>
            )}
            {checkRoll && (
              <li>
                <strong>{checkLabel}:</strong>{' '}
                {Array.isArray(checkRoll.rolls) && checkRoll.rolls.length === 2
                  ? `d20 [${checkRoll.rolls.join(', ')}] → ${checkRoll.chosenValue ?? checkRoll.d20Roll ?? '?'} `
                  : `d20 ${checkRoll.d20Roll ?? checkRoll.roll ?? checkRoll.chosenValue ?? '?'} `}
                {Number.isFinite(checkRoll.modifier)
                  ? `${checkRoll.modifier >= 0 ? '+' : '-'}${Math.abs(checkRoll.modifier)} `
                  : ''}
                = {checkRoll.total ?? '?'}{d?.result?.dc != null ? ` vs DC ${d.result.dc}` : ''}{checkOutcome}
              </li>
            )}
            {ruleReference && (
              <li><strong>Rule:</strong> {ruleReference}</li>
            )}
          </ul>
        );
      }
      case COMBAT_LOG_TYPES.ACTION_ATTACK: {
        // date_of_change: 2025-08-31 - Add detailed roll/modifier/formula breakdown and prefer specific action names
        const specificName = d?.action?.originalAction?.name || d?.action?.name || 'Attack';
        const roll = d?.result?.attackRoll;
        const dmg = d?.result?.damageRoll ?? d?.result?.damage;
        const sneakAttack = d?.result?.sneakAttack;
        const hitMiss = d?.result?.hit !== undefined ? (d.result.hit ? 'Hit' : 'Miss') : undefined;

        // @date_of_change 2025-09-18 - CRITICAL FIX: Handle saving throw abilities (Fire Breath, etc.)
        const effects = d?.result?.effects || [];
        const savingThrowEffects = effects.filter(e => e.type === 'savingThrow') || [];
        const conditionEffects = effects.filter(e => e.type === 'condition') || [];
        const hasSavingThrows = savingThrowEffects.length > 0;
        const hasConditions = conditionEffects.length > 0;

        return (
          <ul className="friendly-fields">
            <li><strong>Action:</strong> {specificName}</li>
            {Array.isArray(d?.targets) && d.targets.length > 0 && (
              <li><strong>Targets:</strong> {d.targets.map(t => t.name).join(', ')}</li>
            )}

            {/* Traditional attack roll display */}
            {roll && !hasSavingThrows && (
              <li>
                <strong>Attack Roll:</strong>{' '}
                {/* Show both d20 rolls when advantage/disadvantage is present, per D&D 5e 2024 rules */}
                {Array.isArray(roll.rolls) && roll.rolls.length === 2 ? (
                  // Both advantage and disadvantage scenarios
                  <>
                    {`d20 [${roll.rolls[0]}, ${roll.rolls[1]}]`}
                    {roll.advantage && roll.disadvantage ? ' (adv/disadv cancel)' :
                     roll.rollType === 'advantage' ? ` → ${recordedD20(roll)} (advantage)` :
                     roll.rollType === 'disadvantage' ? ` → ${recordedD20(roll)} (disadvantage)` : ''}
                    {roll.modifier ? ` + ${roll.modifier}` : ''}
                    {` = ${roll.total}`}
                  </>
                ) : (
                  // Normal single roll
                  `d20 ${recordedD20(roll)} ${Number.isFinite(roll.modifier) ? (roll.modifier >= 0 ? '+ ' + roll.modifier : '- ' + Math.abs(roll.modifier)) : ''} = ${roll.total != null ? roll.total : '?'}`
                )}
                {d?.result?.targetAC ? ` vs AC ${d.result.targetAC}` : ''}
                {roll.isCritical ? ' — CRITICAL HIT!' : roll.isCriticalFail ? ' — CRITICAL MISS!' : ''}
              </li>
            )}

            {/* Saving throw results display */}
            {hasSavingThrows && (
              <li>
                <strong>Saving Throws:</strong>
                <ul style={{marginTop: '5px', paddingLeft: '15px'}}>
                  {savingThrowEffects.map((effect, idx) => {
                    const save = effect.savingThrow;
                    const damage = effect.damage;
                    const saveResult = save?.success ? 'SUCCESS' : 'FAILURE';
                    const advantageText = save?.advantage ? ' (ADV)' : save?.disadvantage ? ' (DIS)' : '';

                    return (
                      <li key={idx} style={{marginBottom: '3px'}}>
                        <strong>{effect.target}:</strong> {save?.ability?.toUpperCase()} save
                        ({save?.roll}+{save?.modifier}{advantageText}) = {save?.total} vs DC {save?.dc}
                        <span style={{color: save?.success ? '#28a745' : '#dc3545'}}> [{saveResult}]</span>
                        {damage && (
                          <div style={{marginLeft: '10px', fontSize: '0.9em'}}>
                            {damage.total === 0 && damage.resistanceApplied?.includes('IMMUNE') ? (
                              <span style={{color: '#6c757d'}}>→ Immune to {damage.type} damage (takes 0 damage)</span>
                            ) : damage.resistanceApplied?.includes('RESISTANT') ? (
                              <span style={{color: '#fd7e14'}}>→ Takes {damage.total} {damage.type} damage (resistant - halved from {damage.originalTotal})</span>
                            ) : damage.resistanceApplied?.includes('VULNERABLE') ? (
                              <span style={{color: '#dc3545'}}>→ Takes {damage.total} {damage.type} damage (vulnerable - doubled)</span>
                            ) : (
                              <span>→ Takes {damage.total} {damage.type} damage{damage.halfDamage ? ' (half on save)' : ''}</span>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {d?.result?.totalDamage && (
                  <div style={{marginTop: '5px', fontWeight: 'bold'}}>
                    Total Damage Dealt: {d.result.totalDamage}
                  </div>
                )}
              </li>
            )}

            {/* Condition effects display for attacks */}
            {hasConditions && (
              <li>
                <strong>Condition Effects:</strong>
                <ul style={{marginTop: '5px', paddingLeft: '15px'}}>
                  {conditionEffects.map((effect, idx) => {
                    const condition = effect.condition;
                    return (
                      <li key={idx} style={{marginBottom: '3px'}}>
                        <strong>{effect.target}:</strong>
                        <span style={{color: '#ffc107'}}> {condition?.name || effect.conditionName || 'Unknown Condition'}</span>
                        {condition?.duration && (
                          <span style={{color: '#6c757d'}}> (duration: {condition.duration} rounds)</span>
                        )}
                        {condition?.source && (
                          <span style={{color: '#17a2b8'}}> from {condition.source}</span>
                        )}
                        {condition?.description && (
                          <div style={{marginLeft: '10px', fontSize: '0.9em', fontStyle: 'italic', color: '#6c757d'}}>
                            {condition.description}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            )}

            {hitMiss && !hasSavingThrows && (
              <li><strong>Result:</strong> {hitMiss}</li>
            )}
            {dmg != null && !hasSavingThrows && (
              <li>
                <strong>Damage:</strong> {recordedDamageTotal(dmg)}
                {dmg.isCritical ? ' (CRITICAL)' : ''}
                {dmg.formula ? ` — ${dmg.formula}` : ''}
                {/* Show individual dice rolls for transparency - D&D 5e 2024 critical shows base + crit dice */}
                {dmg.isCritical && dmg.baseRolls && dmg.criticalRolls ? (
                  ` [base: ${dmg.baseRolls.join(', ')}] + [crit: ${dmg.criticalRolls.join(', ')}]`
                ) : Array.isArray(dmg.rolls) && dmg.rolls.length ? (
                  ` [${dmg.rolls.join(', ')}]`
                ) : ''}
                {dmg.modifier && dmg.modifier !== 0 ? ` + ${dmg.modifier} modifier` : ''}
                {Array.isArray(dmg.details) && dmg.details.length > 0 && (
                  <ul style={{marginTop: '5px', paddingLeft: '15px'}}>
                    {dmg.details.map((detail, idx) => (
                      <li key={`${detail.source || detail.damageType || 'damage'}-${idx}`}>
                        {detail.source || detail.damageType || 'Damage'}: {detail.total}
                        {detail.formula ? ` (${detail.formula})` : ''}
                        {Array.isArray(detail.rolls) && detail.rolls.length > 0 ? ` [${detail.rolls.join(', ')}]` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
            {sneakAttack && !hasSavingThrows && (
              <li>
                <strong>Sneak Attack:</strong> Applied, +{sneakAttack.total} damage ({sneakAttack.dice})
              </li>
            )}
          </ul>
        );
      }
      case COMBAT_LOG_TYPES.ACTION_SPELL: {
        // @date_of_change 2025-09-18 - Enhanced spell display with saving throws and immunity info (matching attack format)
        // Prefer flattened effects on d.result.effects (ActionEconomyProcessor bridge),
        // else fall back to nested d.result.result.effects from CombatManager.
        const effects = d?.result?.effects || d?.result?.result?.effects || [];
        const totalDamage = (d?.result?.totalDamage != null
          ? d.result.totalDamage
          : (Array.isArray(effects) ? effects.reduce((s, e) => s + (e?.damage?.total || 0), 0) : 0));
        const mmMeta = d?.result?.magicMissile || d?.result?.result?.magicMissile;

        // Separate saving throw effects from damage, healing, and condition effects
        const savingThrowEffects = effects.filter(e => e.type === 'savingThrow') || [];
        const damageEffects = effects.filter(e => e.type === 'damage') || [];
        const healingEffects = effects.filter(e => e.type === 'healing') || [];
        const conditionEffects = effects.filter(e => e.type === 'condition') || [];
        const hasSavingThrows = savingThrowEffects.length > 0;
        const hasHealing = healingEffects.length > 0;
        const hasConditions = conditionEffects.length > 0;
        const attackRoll = d?.result?.attackRoll;
        const damageRoll = d?.result?.damageRoll ?? d?.result?.damage;

        return (
          <ul className="friendly-fields">
            <li><strong>Spell:</strong> {d?.action?.name || 'Spell'}</li>
            {Array.isArray(d?.targets) && d.targets.length > 0 && (
              <li><strong>Targets:</strong> {d.targets.map(t => t.name).join(', ')}</li>
            )}

            {attackRoll && !hasSavingThrows && (
              <li>
                <strong>Spell Attack:</strong>{' '}
                {Array.isArray(attackRoll.rolls) && attackRoll.rolls.length === 2
                  ? `d20 [${attackRoll.rolls.join(', ')}]${attackRoll.rollType === 'advantage' ? ` -> ${recordedD20(attackRoll)} (advantage)` : attackRoll.rollType === 'disadvantage' ? ` -> ${recordedD20(attackRoll)} (disadvantage)` : ''}`
                  : `d20 ${recordedD20(attackRoll)}`}
                {Number.isFinite(attackRoll.modifier) ? ` ${attackRoll.modifier >= 0 ? '+' : '-'} ${Math.abs(attackRoll.modifier)}` : ''}
                {` = ${attackRoll.total ?? '?'}`}
                {d?.result?.targetAC != null ? ` vs AC ${d.result.targetAC}` : ''}
              </li>
            )}
            {attackRoll && d?.result?.hit !== undefined && !hasSavingThrows && (
              <li><strong>Result:</strong> {d.result.hit ? 'Hit' : 'Miss'}</li>
            )}
            {damageRoll != null && !hasSavingThrows && (
              <li>
                <strong>Damage:</strong> {recordedDamageTotal(damageRoll)}
                {damageRoll.formula ? ` - ${damageRoll.formula}` : ''}
                {Array.isArray(damageRoll.rolls) && damageRoll.rolls.length > 0 ? ` [${damageRoll.rolls.join(', ')}]` : ''}
              </li>
            )}

            {/* Saving throw results display (same as attacks) */}
            {hasSavingThrows && (
              <li>
                <strong>Saving Throws:</strong>
                <ul style={{marginTop: '5px', paddingLeft: '15px'}}>
                  {savingThrowEffects.map((effect, idx) => {
                    const save = effect.savingThrow;
                    const damage = effect.damage;
                    const saveResult = save?.success ? 'SUCCESS' : 'FAILURE';
                    const advantageText = save?.advantage ? ' (ADV)' : save?.disadvantage ? ' (DIS)' : '';

                    return (
                      <li key={idx} style={{marginBottom: '3px'}}>
                        <strong>{effect.target}:</strong> {save?.ability?.toUpperCase()} save
                        ({save?.roll}+{save?.modifier}{advantageText}) = {save?.total} vs DC {save?.dc}
                        <span style={{color: save?.success ? '#28a745' : '#dc3545'}}> [{saveResult}]</span>
                        {damage && (
                          <div style={{marginLeft: '10px', fontSize: '0.9em'}}>
                            {damage.total === 0 && damage.resistanceApplied?.includes('IMMUNE') ? (
                              <span style={{color: '#6c757d'}}>→ Immune to {damage.type} damage (takes 0 damage)</span>
                            ) : damage.resistanceApplied?.includes('RESISTANT') ? (
                              <span style={{color: '#fd7e14'}}>→ Takes {damage.total} {damage.type} damage (resistant - halved from {damage.originalTotal})</span>
                            ) : damage.resistanceApplied?.includes('VULNERABLE') ? (
                              <span style={{color: '#dc3545'}}>→ Takes {damage.total} {damage.type} damage (vulnerable - doubled)</span>
                            ) : (
                              <span>→ Takes {damage.total} {damage.type} damage{damage.halfDamage ? ' (half on save)' : ''}</span>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {totalDamage > 0 && (
                  <div style={{marginTop: '5px', fontWeight: 'bold'}}>
                    Total Damage Dealt: {totalDamage}
                  </div>
                )}
              </li>
            )}

            {/* Healing effects display */}
            {/* @date_of_change 2025-09-19 - Added healing effects display to match CombatLogManager healing output */}
            {hasHealing && (
              <li>
                <strong>Healing Results:</strong>
                <ul style={{marginTop: '5px', paddingLeft: '15px'}}>
                  {healingEffects.map((effect, idx) => {
                    const healing = effect.healing;
                    return (
                      <li key={idx} style={{marginBottom: '3px'}}>
                        <strong>{effect.target}:</strong> Heals {healing?.total || 0} hit points
                        {healing?.formula && (
                          <span style={{color: '#28a745'}}> ({healing.formula})</span>
                        )}
                        {healing?.rolls && healing.rolls.length > 0 && (
                          <span style={{color: '#6c757d'}}> [{healing.rolls.join(', ')}]</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            )}

            {/* Condition effects display */}
            {/* @date_of_change 2025-09-19 - Added condition effects display for attacks, spells, environmental effects */}
            {hasConditions && (
              <li>
                <strong>Condition Effects:</strong>
                <ul style={{marginTop: '5px', paddingLeft: '15px'}}>
                  {conditionEffects.map((effect, idx) => {
                    const condition = effect.condition;
                    return (
                      <li key={idx} style={{marginBottom: '3px'}}>
                        <strong>{effect.target}:</strong>
                        <span style={{color: '#ffc107'}}> {condition?.name || effect.conditionName || 'Unknown Condition'}</span>
                        {condition?.duration && (
                          <span style={{color: '#6c757d'}}> (duration: {condition.duration} rounds)</span>
                        )}
                        {condition?.source && (
                          <span style={{color: '#17a2b8'}}> from {condition.source}</span>
                        )}
                        {condition?.description && (
                          <div style={{marginLeft: '10px', fontSize: '0.9em', fontStyle: 'italic', color: '#6c757d'}}>
                            {condition.description}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            )}

            {/* Damage effects for non-saving throw spells */}
            {!hasSavingThrows && damageEffects.length > 0 && (
              <>
                <li><strong>Total Damage:</strong> {totalDamage}</li>
                <li>
                  <strong>Effects:</strong>
                  <ul>
                    {damageEffects.map((e, idx) => (
                      <li key={idx}>
                        {CombatLogManager.getCombatantName?.(e.targetId) || e.target}:
                        {e.damage?.total === 0 && e.damage?.resistanceApplied?.includes('IMMUNE') ? (
                          <span style={{color: '#6c757d'}}> Immune to {e.damage?.type} damage (takes 0 damage)</span>
                        ) : e.damage?.resistanceApplied?.includes('RESISTANT') ? (
                          <span style={{color: '#fd7e14'}}> {e.damage?.total || 0} {e.damage?.type || 'damage'} (resistant - halved from {e.damage?.originalTotal})</span>
                        ) : e.damage?.resistanceApplied?.includes('VULNERABLE') ? (
                          <span style={{color: '#dc3545'}}> {e.damage?.total || 0} {e.damage?.type || 'damage'} (vulnerable - doubled)</span>
                        ) : (
                          <span> {e.damage?.total || 0} {e.damage?.type || 'damage'}</span>
                        )}
                        {e.damage?.formula ? ` — ${e.damage.formula}` : ''}
                      </li>
                    ))}
                  </ul>
                </li>
              </>
            )}

            {/* Magic Missile special handling */}
            {mmMeta && (
              <li>
                <strong>Magic Missile:</strong> {mmMeta.totalDarts} darts at level {mmMeta.slotLevel}
              </li>
            )}
          </ul>
        );
      }
      case COMBAT_LOG_TYPES.DAMAGE_DEALT: {
        const targetName = d?.targetId ? CombatLogManager.getCombatantName(d.targetId) : 'target';
        return (
          <ul className="friendly-fields">
            <li><strong>Target:</strong> {targetName}</li>
            <li><strong>Damage:</strong> {d?.damage}</li>
            {d?.damageType && <li><strong>Type:</strong> {d.damageType}</li>}
          </ul>
        );
      }
      case COMBAT_LOG_TYPES.HEALING_APPLIED: {
        const targetName = d?.targetId ? CombatLogManager.getCombatantName(d.targetId) : 'target';
        return (
          <ul className="friendly-fields">
            <li><strong>Target:</strong> {targetName}</li>
            <li><strong>Healing:</strong> {d?.healing}</li>
          </ul>
        );
      }
      case COMBAT_LOG_TYPES.ENVIRONMENT_EFFECT: {
        // @date_of_change 2025-09-19 - Added environmental effects display for map-based conditions
        const targetName = d?.targetId ? CombatLogManager.getCombatantName(d.targetId) : 'target';
        const sourceName = d?.sourceId ? CombatLogManager.getCombatantName(d.sourceId) : d?.source || 'Environment';
        const effects = d?.effects || [];

        return (
          <ul className="friendly-fields">
            <li><strong>Target:</strong> {targetName}</li>
            <li><strong>Source:</strong> {sourceName}</li>
            {effects.length > 0 && (
              <li>
                <strong>Effects:</strong>
                <ul style={{marginTop: '5px', paddingLeft: '15px'}}>
                  {effects.map((effect, idx) => (
                    <li key={idx} style={{marginBottom: '3px'}}>
                      {effect.type === 'condition' && (
                        <>
                          <span style={{color: '#ffc107'}}>{effect.condition?.name || effect.conditionName || 'Unknown Condition'}</span>
                          {effect.condition?.duration && (
                            <span style={{color: '#6c757d'}}> (duration: {effect.condition.duration} rounds)</span>
                          )}
                        </>
                      )}
                      {effect.type === 'damage' && (
                        <span style={{color: '#dc3545'}}>{effect.damage?.total || 0} {effect.damage?.type || 'damage'}</span>
                      )}
                      {effect.description && (
                        <div style={{marginLeft: '10px', fontSize: '0.9em', fontStyle: 'italic', color: '#6c757d'}}>
                          {effect.description}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            )}
          </ul>
        );
      }
      default:
        return null;
    }
  };

  // Filter combat log entries
  const filteredLog = combatLog.filter(entry => {
    if (filter === 'all') return true;
    if (filter === 'actions') return entry.type.startsWith('ACTION_');
    if (filter === 'damage') return entry.type === COMBAT_LOG_TYPES.DAMAGE_DEALT;
    if (filter === 'healing') return entry.type === COMBAT_LOG_TYPES.HEALING_APPLIED;
    if (filter === 'conditions') return entry.type.includes('CONDITION');
    if (filter === 'turns') return entry.type.includes('TURN_') || entry.type.includes('ROUND_');
    return true;
  });

  // Get icon for log entry type
  const getEntryIcon = (type) => {
    const iconMap = {
      [COMBAT_LOG_TYPES.COMBAT_START]: '🎲',
      [COMBAT_LOG_TYPES.COMBAT_END]: '🏁',
      [COMBAT_LOG_TYPES.ROUND_START]: '🔄',
      [COMBAT_LOG_TYPES.TURN_START]: '➡️',
      [COMBAT_LOG_TYPES.TURN_END]: '⏸️',
      [COMBAT_LOG_TYPES.ACTION_STANDARD]: '🎯',
      [COMBAT_LOG_TYPES.ACTION_ATTACK]: '⚔️',
      [COMBAT_LOG_TYPES.ACTION_SPELL]: '✨',
      [COMBAT_LOG_TYPES.ACTION_MOVE]: '🏃',
      [COMBAT_LOG_TYPES.ACTION_BONUS]: '⚡',
      [COMBAT_LOG_TYPES.ACTION_REACTION]: '🛡️',
      [COMBAT_LOG_TYPES.ACTION_FREE]: '💬',
      [COMBAT_LOG_TYPES.DAMAGE_DEALT]: '💥',
      [COMBAT_LOG_TYPES.HEALING_APPLIED]: '💚',
      [COMBAT_LOG_TYPES.CONDITION_APPLIED]: '😵',
      [COMBAT_LOG_TYPES.CONDITION_REMOVED]: '😌',
      [COMBAT_LOG_TYPES.TOKEN_MOVED]: '🚶',
      [COMBAT_LOG_TYPES.ENVIRONMENT_EFFECT]: '🌪️'
    };
    return iconMap[type] || '📝';
  };

  // Build a user-friendly summary line per entry
  const formatEntrySummary = useCallback((entry) => {
    const actorName = CombatLogManager.getCombatantName(entry.combatantId) || 'Unknown';
    const d = entry?.data || {};
    switch (entry.type) {
      case COMBAT_LOG_TYPES.ACTION_STANDARD:
      case COMBAT_LOG_TYPES.ACTION_BONUS:
      case COMBAT_LOG_TYPES.ACTION_REACTION: {
        const actionName = d?.action?.originalAction?.name || d?.action?.name || d?.result?.actionName || 'Action';
        const targets = Array.isArray(d?.targets) ? d.targets.map(t => t.name).join(', ') : '';
        const effect = d?.result?.effect || d?.action?.description || '';
        const check = d?.result?.check || d?.result?.skillCheck || d?.result?.abilityCheck;
        const checkRoll = d?.result?.checkRoll || check;
        let summary = `${actorName} uses ${actionName}${targets ? ` on ${targets}` : ''}`;
        if (checkRoll) {
          const checkLabel = check?.skill || (check?.ability ? `${check.ability} ability` : 'Check');
          const modifier = Number.isFinite(checkRoll.modifier)
            ? `${checkRoll.modifier >= 0 ? '+' : '-'}${Math.abs(checkRoll.modifier)}`
            : '';
          summary += ` - ${checkLabel} d20 ${checkRoll.d20Roll ?? checkRoll.roll ?? checkRoll.chosenValue ?? '?'} ${modifier} = ${checkRoll.total ?? '?'}`;
          if (d?.result?.dc != null) {
            summary += ` vs DC ${d.result.dc}${d.result.checkSuccess ? ' — SUCCESS' : ' — FAILURE'}`;
          }
        } else if (effect) {
          summary += ` - ${effect}`;
        }
        return summary;
      }
      case COMBAT_LOG_TYPES.ACTION_ATTACK: {
        const actionName = d?.action?.originalAction?.name || d?.action?.name || 'Attack';
        const targets = Array.isArray(d?.targets) ? d.targets.map(t => t.name).join(', ') : '';
        const result = d?.result || {};
        
        // Handle multiattack display - D&D 5e 2024 multiattack support
        // @date_of_change 2025-09-01
        if (result.isComponentAttack) {
          const parentAction = result.parentAction || 'Multiattack';
          const componentText = result.totalComponents > 1 
            ? ` (${result.componentIndex}/${result.totalComponents} of ${parentAction})`
            : '';
          
          let summary = `${actorName} ${actionName}${componentText}${targets ? ` on ${targets}` : ''}`;
          
          // Add attack roll details for component attacks
          if (result.attackRoll) {
            const roll = result.attackRoll;
            const mod = Number.isFinite(roll.modifier) ? (roll.modifier >= 0 ? `+${roll.modifier}` : `${roll.modifier}`) : '';
            const total = roll.total != null ? roll.total : '?';
            
            // Show both d20 rolls when advantage/disadvantage is present
            let rollText;
            if (Array.isArray(roll.rolls) && roll.rolls.length === 2) {
              rollText = `rolls d20 [${roll.rolls[0]}, ${roll.rolls[1]}]`;
              if (roll.advantage && roll.disadvantage) {
                rollText += ' (adv/disadv cancel)';
              } else if (roll.rollType === 'advantage') {
                rollText += ` → ${recordedD20(roll)} (advantage)`;
              } else if (roll.rollType === 'disadvantage') {
                rollText += ` → ${recordedD20(roll)} (disadvantage)`;
              }
              rollText += ` ${mod} = ${total}`;
            } else {
              const d20 = recordedD20(roll);
              rollText = `rolls d20 ${d20} ${mod} = ${total}`;
            }
            
            summary += ` - ${rollText}`;
            
            if (result.targetAC) {
              summary += ` vs AC ${result.targetAC}`;
            }
            
            if (roll.isCritical) {
              summary += ' - CRITICAL HIT!';
            } else if (roll.isCriticalFailure) {
              summary += ' - CRITICAL MISS';
            } else if (result.hit === true) {
              summary += ' - HIT';
            } else if (result.hit === false) {
              summary += ' - MISS';
            }
          }
          
          // Add damage for component attacks
          if (result.hit && result.damageRoll) {
            const dmgRoll = result.damageRoll;
            summary += ` - ${dmgRoll.total} damage`;
            if (dmgRoll.isCritical) {
              summary += ' (CRITICAL)';
            }
          } else if (result.hit && result.damage) {
            // @date_of_change 2025-09-15 - Fix "[object Object]" damage display
            const damageAmount = typeof result.damage === 'object' ? result.damage.total : result.damage;
            summary += ` - ${damageAmount} damage`;
          }
          
          return summary;
        }
        
        // Handle multiattack start action
        if (d?.result?.componentAttacks) {
          const componentList = d.result.componentAttacks.join(', ');
          return `${actorName} uses ${actionName} (${d.result.totalAttacks} attacks: ${componentList})`;
        }
        
        // @date_of_change 2025-09-18 - Handle saving throw abilities in summary
        const effects = result?.effects || [];
        const savingThrowEffects = effects.filter(e => e.type === 'savingThrow') || [];
        const conditionEffects = effects.filter(e => e.type === 'condition') || [];
        const hasSavingThrows = savingThrowEffects.length > 0;
        const hasConditions = conditionEffects.length > 0;

        // Build detailed attack summary with rolls for regular attacks
        let summary = `${actorName} uses ${actionName}${targets ? ` on ${targets}` : ''}`;

        // Handle saving throw abilities differently from attack roll abilities
        if (hasSavingThrows) {
          const successCount = savingThrowEffects.filter(e => e.savingThrow?.success).length;
          const failureCount = savingThrowEffects.length - successCount;
          const totalDamage = result.totalDamage || savingThrowEffects.reduce((sum, e) => sum + (e.damage?.total || 0), 0);

          summary += ` - ${failureCount} failed save${failureCount !== 1 ? 's' : ''}`;
          if (successCount > 0) {
            summary += `, ${successCount} succeeded`;
          }
          if (totalDamage > 0) {
            summary += ` - ${totalDamage} total damage`;
          }
          if (hasConditions) {
            const conditionNames = conditionEffects.map(e => e.condition?.name || e.conditionName || 'Unknown').join(', ');
            summary += ` - applies ${conditionNames}`;
          }

          return summary;
        }

        if (result.attackRoll) {
          const roll = result.attackRoll;
          const mod = Number.isFinite(roll.modifier) ? (roll.modifier >= 0 ? `+${roll.modifier}` : `${roll.modifier}`) : '';
          const total = roll.total != null ? roll.total : '?';
          
          // Show both d20 rolls when advantage/disadvantage is present - D&D 5e 2024
          let rollText;
          if (Array.isArray(roll.rolls) && roll.rolls.length === 2) {
            // Both advantage and disadvantage scenarios
            rollText = `rolls d20 [${roll.rolls[0]}, ${roll.rolls[1]}]`;
            if (roll.advantage && roll.disadvantage) {
              rollText += ' (adv/disadv cancel)';
            } else if (roll.rollType === 'advantage') {
              rollText += ` → ${recordedD20(roll)} (advantage)`;
            } else if (roll.rollType === 'disadvantage') {
              rollText += ` → ${recordedD20(roll)} (disadvantage)`;
            }
            rollText += ` ${mod} = ${total}`;
          } else {
            // Normal single roll
            const d20 = recordedD20(roll);
            rollText = `rolls d20 ${d20} ${mod} = ${total}`;
          }
          
          summary += ` - ${rollText}`;
          
          if (result.targetAC) {
            summary += ` vs AC ${result.targetAC}`;
          }
          
          if (roll.isCritical) {
            summary += ' - CRITICAL HIT!';
          } else if (roll.isCriticalFailure) {
            summary += ' - CRITICAL MISS';
          } else if (result.hit === true) {
            summary += ' - HIT';
          } else if (result.hit === false) {
            summary += ' - MISS';
          }
        } else {
          // Fallback for simple hit/miss
          const hitTxt = result.hit === true ? 'HIT' : (result.hit === false ? 'MISS' : '');
          if (hitTxt) summary += ` (${hitTxt})`;
        }
        
        // Add damage if attack hit
        if (result.hit && result.damageRoll) {
          const dmgRoll = result.damageRoll;
          summary += ` - ${dmgRoll.total} damage`;
          if (dmgRoll.isCritical) {
            summary += ' (critical)';
          }
        } else if (result.hit && result.damage) {
          // @date_of_change 2025-09-15 - Fix "[object Object]" damage display
          const damageAmount = typeof result.damage === 'object' ? result.damage.total : result.damage;
          summary += ` - ${damageAmount} damage`;
        }

        if (result.sneakAttack) {
          summary += ` - Sneak Attack +${result.sneakAttack.total} (${result.sneakAttack.dice})`;
        }

        // Add condition information for regular attacks
        if (hasConditions) {
          const conditionNames = conditionEffects.map(e => e.condition?.name || e.conditionName || 'Unknown').join(', ');
          summary += ` - applies ${conditionNames}`;
        }

        return summary;
      }
      case COMBAT_LOG_TYPES.ACTION_SPELL: {
        // date_of_change: 2025-09-18
        // Consistent summary with dragon breath (saving-throw abilities):
        // Show failure/success counts and total damage when saves are involved.
        const spellName = d?.action?.name || 'Spell';
        const targets = Array.isArray(d?.targets) ? d.targets.map(t => t.name).join(', ') : '';
        const result = d?.result || {};
        const effects = result?.effects || result?.result?.effects || [];
        const savingThrowEffects = Array.isArray(effects) ? effects.filter(e => e.type === 'savingThrow') : [];
        const damageEffects = Array.isArray(effects) ? effects.filter(e => e.type === 'damage') : [];
        const conditionEffects = Array.isArray(effects) ? effects.filter(e => e.type === 'condition') : [];
        const hasSavingThrows = savingThrowEffects.length > 0;
        const hasConditions = conditionEffects.length > 0;
        let summary = `${actorName} casts ${spellName}${targets ? ` on ${targets}` : ''}`;

        if (hasSavingThrows) {
          const successCount = savingThrowEffects.filter(e => e.savingThrow?.success).length;
          const failureCount = savingThrowEffects.length - successCount;
          const totalDamage = (result.totalDamage != null)
            ? result.totalDamage
            : savingThrowEffects.reduce((sum, e) => sum + (e.damage?.total || 0), 0);
          summary += ` - ${failureCount} failed save${failureCount !== 1 ? 's' : ''}`;
          if (successCount > 0) summary += `, ${successCount} succeeded`;
          if (totalDamage > 0) summary += ` - ${totalDamage} total damage`;
          if (hasConditions) {
            const conditionNames = conditionEffects.map(e => e.condition?.name || e.conditionName || 'Unknown').join(', ');
            summary += ` - applies ${conditionNames}`;
          }
          return summary;
        }

        if (result.attackRoll) {
          const roll = result.attackRoll;
          if (Array.isArray(roll.rolls) && roll.rolls.length === 2) {
            summary += ` - d20 [${roll.rolls.join(', ')}]`;
            if (roll.rollType === 'advantage') summary += ` -> ${recordedD20(roll)} (advantage)`;
            if (roll.rollType === 'disadvantage') summary += ` -> ${recordedD20(roll)} (disadvantage)`;
          } else {
            summary += ` - d20 ${recordedD20(roll)}`;
          }
          if (Number.isFinite(roll.modifier)) summary += ` ${roll.modifier >= 0 ? '+' : '-'}${Math.abs(roll.modifier)}`;
          summary += ` = ${roll.total ?? '?'}`;
          if (result.targetAC != null) summary += ` vs AC ${result.targetAC}`;
          if (result.hit === true) summary += ' - HIT';
          if (result.hit === false) summary += ' - MISS';
        }

        // Non-save spells: show total damage and healing if available
        const totalDamage = (result.totalDamage != null)
          ? result.totalDamage
          : damageEffects.reduce((s, e) => s + (e.damage?.total || 0), 0);
        const totalHealing = (result.totalHealing != null)
          ? result.totalHealing
          : effects.filter(e => e.type === 'healing').reduce((s, e) => s + (e.healing?.total || 0), 0);

        if (totalDamage > 0) {
          summary += ` - ${totalDamage} total damage`;
        }
        if (totalHealing > 0) {
          summary += ` - ${totalHealing} total healing`;
        }
        if (hasConditions) {
          const conditionNames = conditionEffects.map(e => e.condition?.name || e.conditionName || 'Unknown').join(', ');
          summary += ` - applies ${conditionNames}`;
        }
        return summary;
      }
      case COMBAT_LOG_TYPES.DAMAGE_DEALT: {
        const target = d?.targetId ? CombatLogManager.getCombatantName(d.targetId) : 'target';
        const amount = d?.damage ?? '?';
        const dtype = d?.damageType || 'damage';
        return `${actorName} deals ${amount} ${dtype} to ${target}`;
      }
      case COMBAT_LOG_TYPES.HEALING_APPLIED: {
        const target = d?.targetId ? CombatLogManager.getCombatantName(d.targetId) : 'target';
        const amount = d?.healing ?? '?';
        return `${actorName} heals ${target} for ${amount}`;
      }
      case COMBAT_LOG_TYPES.CONDITION_APPLIED: {
        const target = d?.targetId ? CombatLogManager.getCombatantName(d.targetId) : 'target';
        const cond = d?.condition || 'Condition';
        return `${actorName} applies ${cond} to ${target}`;
      }
      case COMBAT_LOG_TYPES.TOKEN_MOVED: {
        if (d?.mechanicalEventType === 'reaction:timeout') return `${actorName} · ${entry.description}`;
        const dist = d?.distance != null ? `${d.distance}ft` : '';
        return `${actorName} moves ${dist}`.trim();
      }
      case COMBAT_LOG_TYPES.ENVIRONMENT_EFFECT: {
        // @date_of_change 2025-09-19 - Added environmental effects summary
        const target = d?.targetId ? CombatLogManager.getCombatantName(d.targetId) : 'target';
        const source = d?.sourceId ? CombatLogManager.getCombatantName(d.sourceId) : d?.source || 'Environment';
        const effects = d?.effects || [];

        let summary = `${target} affected by ${source}`;

        if (effects.length > 0) {
          const conditionEffects = effects.filter(e => e.type === 'condition');
          const damageEffects = effects.filter(e => e.type === 'damage');

          if (conditionEffects.length > 0) {
            const conditionNames = conditionEffects.map(e => e.condition?.name || e.conditionName || 'Unknown').join(', ');
            summary += ` - applies ${conditionNames}`;
          }

          if (damageEffects.length > 0) {
            const totalDamage = damageEffects.reduce((sum, e) => sum + (e.damage?.total || 0), 0);
            summary += ` - ${totalDamage} damage`;
          }
        }

        return summary;
      }
      case COMBAT_LOG_TYPES.TURN_START:
      case COMBAT_LOG_TYPES.TURN_END:
      case COMBAT_LOG_TYPES.ROUND_START:
      case COMBAT_LOG_TYPES.COMBAT_START:
      case COMBAT_LOG_TYPES.COMBAT_END:
      default:
        return entry.description || `${entry.type}`;
    }
  }, []);

  // Get CSS class for log entry type
  const getEntryClass = (type) => {
    if (type.startsWith('ACTION_')) return 'log-action';
    if (type === COMBAT_LOG_TYPES.DAMAGE_DEALT) return 'log-damage';
    if (type === COMBAT_LOG_TYPES.HEALING_APPLIED) return 'log-healing';
    if (type.includes('CONDITION')) return 'log-condition';
    if (type.includes('TURN_') || type.includes('ROUND_')) return 'log-turn';
    return 'log-general';
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  if (collapsed) {
    return (
      <div className={`combat-log-collapsed ${className}`}>
        <div className="combat-log-header collapsed">
          <h4>Combat Log ({combatLog.length} entries)</h4>
        </div>
      </div>
    );
  }

  return (
    <div className={`combat-log ${className}`}>
      {/* Header */}
      <div className="combat-log-header">
        <h4>Combat Log</h4>
        <div className="combat-log-controls">
          {isGameMaster && (
            <>
              <button
                className={`btn btn-sm btn-secondary ${editMode ? 'active' : ''}`}
                onClick={() => {
                  if (editMode) cancelEditing();
                  onEditModeChange?.(!editMode);
                }}
                disabled={combatLog.length === 0}
                title="Edit combat log descriptions"
                aria-pressed={editMode}
              >
                {editMode ? 'Finish Editing' : 'Edit'}
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  const opening = !showRewindControls;
                  setShowRewindControls(opening);
                  if (opening) onRefreshRewindPoints?.();
                }}
                disabled={!onRefreshRewindPoints && displayedRewindPoints.length === 0}
                title="Show rewind controls"
              >
                Rewind
              </button>
            </>
          )}
          <button 
            className="btn btn-sm btn-secondary"
            onClick={handleExport}
            disabled={combatLog.length === 0}
            title="Export as JSON"
          >
            JSON
          </button>
          {/* GAP-012: Markdown export — date_of_change: 2026-05-20 */}
          <button
            className="btn btn-sm btn-secondary"
            onClick={handleExportMarkdown}
            disabled={combatLog.length === 0}
            title="Export as Markdown"
            data-testid="export-markdown-btn"
          >
            MD
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setShowSummary((v) => !v)}
            disabled={combatLog.length === 0}
            title="Show summary statistics"
            data-testid="show-summary-btn"
          >
            Stats
          </button>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={refreshCombatLog}
            title="Refresh log"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="combat-log-filters">
        <select 
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Events</option>
          <option value="actions">Actions</option>
          <option value="damage">Damage</option>
          <option value="healing">Healing</option>
          <option value="conditions">Conditions</option>
          <option value="turns">Turns</option>
        </select>
        <span className="entry-count">{filteredLog.length} entries</span>
      </div>

      {/* Rewind Controls */}
      {showRewindControls && (
        <div className="rewind-controls">
          <h5>Authoritative rewind points</h5>
          <p className="rewind-guidance">Rewind restores the server state for everyone in this BattleRoom.</p>
          {(rewindError || localRewindError) && <p className="rewind-error" role="alert">{rewindError || localRewindError}</p>}
          {rewindPending && <p className="rewind-guidance" role="status">Updating rewind history…</p>}
          <div className="rewind-points">
            {displayedRewindPoints.map((point, index) => (
              <div 
                key={point.checkpointId || point.index || index}
                className={`rewind-point ${selectedRewindPoint === index ? 'selected' : ''}`}
                onClick={() => setSelectedRewindPoint(index)}
              >
                <div className="rewind-point-info">
                  <span className="rewind-description">{point.label || point.description}</span>
                  <span className="rewind-timestamp">Round {point.round}, turn {point.turn} · {formatTimestamp(point.createdAt || point.timestamp)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="rewind-actions">
            <button 
              className="btn btn-warning"
              onClick={reviewRewind}
              disabled={selectedRewindPoint === -1 || rewindPending || previewPending}
            >
              {previewPending ? 'Loading Preview…' : 'Review Rewind'}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowRewindControls(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <BattleRoomDialog
        visible={Boolean(rewindConfirmation)}
        eyebrow="Authoritative rewind"
        title="Rewind the BattleRoom?"
        description="This replaces the active scene for every connected participant and prunes later durable combat events. This rewind cannot be automatically undone."
        onRequestClose={rewindPending ? undefined : () => { setRewindConfirmation(null); setRewindPreview(null); setRewindReason(''); setLocalRewindError(''); }}
        footer={(
          <>
            <button type="button" className="battle-room-dialog__button battle-room-dialog__button--quiet" disabled={rewindPending} onClick={() => { setRewindConfirmation(null); setRewindPreview(null); setRewindReason(''); }}>Cancel</button>
            <button type="button" className="battle-room-dialog__button battle-room-dialog__button--primary" disabled={rewindPending || !rewindReason.trim() || (onRewindPreview && !rewindPreview)} onClick={handleRewind}>{rewindPending ? 'Rewinding…' : 'Rewind for Everyone'}</button>
          </>
        )}
      >
        <p className="rewind-confirmation__label">{rewindConfirmation?.label || rewindConfirmation?.description}</p>
        <p className="battle-room-dialog__note">Round {rewindConfirmation?.round}, turn {rewindConfirmation?.turn}</p>
        {rewindPreview && (
          <div className="rewind-preview" aria-label="Rewind changes">
            <dl>
              <div><dt>Timeline</dt><dd>Round {rewindPreview.from?.round}, turn {rewindPreview.from?.turn} → Round {rewindPreview.to?.round}, turn {rewindPreview.to?.turn}</dd></div>
              <div><dt>Tokens changed</dt><dd>{rewindPreview.tokens?.length || 0}</dd></div>
              <div><dt>Events superseded</dt><dd>{rewindPreview.prunedEventCount || 0}</dd></div>
              <div><dt>Initiative</dt><dd>{rewindPreview.initiativeChanged ? 'Will change' : 'Unchanged'}</dd></div>
              <div><dt>Fog operations</dt><dd>{rewindPreview.fog?.currentOperations || 0} → {rewindPreview.fog?.restoredOperations || 0}</dd></div>
            </dl>
            {rewindPreview.tokens?.length > 0 && <details><summary>Review affected tokens</summary><ul>{rewindPreview.tokens.map((token) => <li key={token.tokenId}><strong>{token.name}</strong>: {token.change}{Object.keys(token.changes || {}).length ? ` (${Object.keys(token.changes).join(', ')})` : ''}</li>)}</ul></details>}
            {rewindPreview.externalReconciliationWarnings?.length > 0 && <p className="rewind-error">Some superseded events have external receipts and may require manual reconciliation.</p>}
            {rewindPreview.bounded && <p className="battle-room-dialog__note">This is a bounded summary; additional changes exist.</p>}
          </div>
        )}
        <label className="battle-room-dialog__field">
          <span className="battle-room-dialog__label">Reason for rewind</span>
          <textarea aria-label="Reason for rewind" className="battle-room-dialog__input" value={rewindReason} maxLength={240} rows={2} onChange={(event) => setRewindReason(event.target.value)} placeholder="Why is the table being restored?" data-battle-dialog-autofocus />
          <span className="battle-room-dialog__hint">Saved in the durable table audit · {rewindReason.length}/240</span>
        </label>
        {(rewindError || localRewindError) && <p className="rewind-error" role="alert">{rewindError || localRewindError}</p>}
      </BattleRoomDialog>

      {/* GAP-012: Summary statistics panel — date_of_change: 2026-05-20 */}
      {showSummary && combatLog.length > 0 && (() => {
        const summaryStats = CombatLogManager.buildCombatSummaryStats();
        const rows = Object.values(summaryStats);
        if (rows.length === 0) return null;
        return (
          <div className="combat-log-summary" style={{ padding: '8px', borderBottom: '1px solid #374151', background: '#1f2937' }}>
            <h5 style={{ color: '#d1d5db', margin: '0 0 6px 0', fontSize: '0.8em' }}>📊 Summary Statistics</h5>
            <table style={{ width: '100%', fontSize: '0.75em', borderCollapse: 'collapse', color: '#d1d5db' }}>
              <thead>
                <tr style={{ color: '#9ca3af', borderBottom: '1px solid #374151' }}>
                  <th style={{ textAlign: 'left', padding: '2px 4px' }}>Name</th>
                  <th style={{ textAlign: 'right', padding: '2px 4px' }}>Dmg ▲</th>
                  <th style={{ textAlign: 'right', padding: '2px 4px' }}>Dmg ▼</th>
                  <th style={{ textAlign: 'right', padding: '2px 4px' }}>Heal</th>
                  <th style={{ textAlign: 'right', padding: '2px 4px' }}>Kills</th>
                  <th style={{ textAlign: 'right', padding: '2px 4px' }}>Crits</th>
                  <th style={{ textAlign: 'right', padding: '2px 4px' }}>Spells</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #1f2937' }}>
                    <td style={{ padding: '2px 4px', fontWeight: 'bold' }}>{s.name}</td>
                    <td style={{ textAlign: 'right', padding: '2px 4px', color: '#f87171' }}>{s.damageDealt}</td>
                    <td style={{ textAlign: 'right', padding: '2px 4px', color: '#fb923c' }}>{s.damageReceived}</td>
                    <td style={{ textAlign: 'right', padding: '2px 4px', color: '#4ade80' }}>{s.healingDone}</td>
                    <td style={{ textAlign: 'right', padding: '2px 4px' }}>{s.kills}</td>
                    <td style={{ textAlign: 'right', padding: '2px 4px', color: '#facc15' }}>{s.crits}</td>
                    <td style={{ textAlign: 'right', padding: '2px 4px', color: '#818cf8' }}>{s.spellsCast}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Combat Log Entries */}
      <div className="combat-log-entries">
        {filteredLog.length === 0 ? (
          <div className="no-entries">
            <p>No combat events logged yet.</p>
            <p>Start combat and take actions to see them appear here.</p>
          </div>
        ) : (
          filteredLog.map((entry, index) => (
            <div 
              key={entry.id || index}
              className={`log-entry ${getEntryClass(entry.type)}`}
            >
              <div className="log-entry-header" onClick={() => toggleEntryExpansion(index)}>
                <div className="log-entry-main">
                  <span className="log-icon">{getEntryIcon(entry.type)}</span>
                  <span className="log-description">{formatEntrySummary(entry)}</span>
                  <span className="log-timestamp">{formatTimestamp(entry.timestamp)}</span>
                </div>
                <div className="log-entry-meta">
                  <span className="log-round">R{entry.round}</span>
                  {entry.turn > 0 && <span className="log-turn">T{entry.turn}</span>}
                  {editMode && isGameMaster && (
                    <button
                      type="button"
                      className="log-entry-edit-button"
                      onClick={(event) => {
                        event.stopPropagation();
                        beginEditing(entry);
                      }}
                      aria-label={`Edit ${formatEntrySummary(entry)}`}
                    >
                      Edit
                    </button>
                  )}
                  {expandedEntries.has(index) ? '▼' : '▶'}
                </div>
              </div>

              {editingEntryId === entry.id && (
                <div className="log-entry-editor">
                  <label htmlFor={`combat-log-edit-${entry.id}`}>Description</label>
                  <textarea
                    id={`combat-log-edit-${entry.id}`}
                    value={editDraft}
                    onChange={(event) => setEditDraft(event.target.value)}
                    rows={3}
                    autoFocus
                  />
                  <p>Structured action data remains unchanged. This edit is recorded in the audit history.</p>
                  <div className="log-entry-editor-actions">
                    <button type="button" onClick={saveEntryEdit} disabled={!editDraft.trim()}>Save</button>
                    <button type="button" onClick={cancelEditing}>Cancel</button>
                  </div>
                </div>
              )}
              
              {expandedEntries.has(index) && (
                <div className="log-entry-details">
                  <div className="log-data">
                    <strong>Type:</strong> {entry.type}
                  </div>
                  {entry.combatantId && (
                    <div className="log-data">
                      <strong>Actor:</strong> {CombatLogManager.getCombatantName(entry.combatantId)}
                    </div>
                  )}
                  {/* Friendly details */}
                  {renderFriendlyDetails(entry)}

                  {/* Raw JSON toggle */}
                  {entry.data && Object.keys(entry.data).length > 0 && (
                    <div className="log-data">
                      <button
                        className="btn btn-xs btn-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowRawFor(prev => {
                            const ns = new Set(prev);
                            if (ns.has(index)) ns.delete(index); else ns.add(index);
                            return ns;
                          });
                        }}
                      >
                        {showRawFor.has(index) ? 'Hide Raw' : 'Show Raw'}
                      </button>
                      {showRawFor.has(index) && (
                        <pre className="log-data-json">
                          {JSON.stringify(entry.data, null, 2)}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="combat-log-footer">
        <div className="session-info">
          {CombatLogManager.combatStarted ? (
            <>
              <span>⚡ Combat Active</span>
              <span>Round {CombatLogManager.currentRound}</span>
            </>
          ) : (
            <span>⏸️ Combat Inactive</span>
          )}
        </div>
      </div>
    </div>
  );
};

CombatLog.propTypes = {
  onRewindRequest: PropTypes.func,
  onExportSession: PropTypes.func,
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  isGameMaster: PropTypes.bool,
  editMode: PropTypes.bool,
  onEditModeChange: PropTypes.func,
  currentUser: PropTypes.object,
  authoritativeRewindPoints: PropTypes.array,
  rewindPending: PropTypes.bool,
  rewindError: PropTypes.string,
  onRewindPreview: PropTypes.func,
  onRefreshRewindPoints: PropTypes.func
};

export default CombatLog;
